import { useMemo, useState } from "react";
import { products, categories } from "../products";
import ProductCard from "../components/ProductCard";
import Seo from "../components/Seo";

type Filter = "All" | (typeof categories)[number];

export default function Catalog() {
  const [filter, setFilter] = useState<Filter>("All");
  const [query, setQuery] = useState("");

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return products.filter((p) => {
      const matchesCat = filter === "All" || p.category === filter;
      if (!matchesCat) return false;
      if (!q) return true;
      return (
        p.proprietaryName.toLowerCase().includes(q) ||
        p.pharmaceuticalName.toLowerCase().includes(q) ||
        p.tagline.toLowerCase().includes(q) ||
        p.indications.some((i) => i.toLowerCase().includes(q))
      );
    });
  }, [filter, query]);

  const counts = useMemo(() => {
    const base: Record<string, number> = { All: products.length };
    for (const c of categories) base[c] = products.filter((p) => p.category === c).length;
    return base;
  }, []);

  const itemListLd = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: "OLYMPUS MEDS — Complete Catalog",
    numberOfItems: products.length,
    itemListElement: products.map((p, i) => ({
      "@type": "ListItem",
      position: i + 1,
      url: `https://olympusroids.com/product/${p.slug}`,
      name: `${p.proprietaryName} — ${p.pharmaceuticalName}`,
    })),
  };

  return (
    <section className="catalog">
      <Seo
        title="Complete Catalog"
        description={`Browse all ${products.length} OLYMPUS MEDS formulations — pharmaceutical-grade oral, injectable, and master blend compounds. Filter by category or search by molecule.`}
        path="/catalog"
        jsonLd={itemListLd}
      />
      <div className="catalog__inner">
        <div className="catalog__heading">
          <span className="eyebrow">Catalog</span>
          <h1 className="section-title">Complete Catalog</h1>
          <p className="section-sub">
            {products.length} formulations · oral, injectable, and master blends.
          </p>
        </div>

        <div className="catalog__controls">
          <div className="catalog__filters">
            <button
              className={`chip ${filter === "All" ? "chip--active" : ""}`}
              onClick={() => setFilter("All")}
            >
              All <span className="chip__count">{counts["All"]}</span>
            </button>
            {categories.map((c) => (
              <button
                key={c}
                className={`chip ${filter === c ? "chip--active" : ""}`}
                onClick={() => setFilter(c)}
              >
                {c} <span className="chip__count">{counts[c] ?? 0}</span>
              </button>
            ))}
          </div>
          <div className="catalog__search">
            <span className="catalog__search-icon">⌕</span>
            <input
              type="search"
              placeholder="Search name, molecule, or indication…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
        </div>

        {filtered.length === 0 ? (
          <div className="catalog__empty">
            <div className="catalog__empty-title">No formulations match</div>
            <div className="catalog__empty-sub">
              Try a different category or search term.
            </div>
          </div>
        ) : (
          <div className="product-grid">
            {filtered.map((p) => (
              <ProductCard key={p.slug} product={p} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
